import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, Star, PauseCircle, PlayCircle, Gift, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EditPractitionerDialog } from "@/components/admin/EditPractitionerDialog";

export const Approvals = ({ onChange }) => {
  const [practitioners, setPractitioners] = useState([]);
  const [learnPending, setLearnPending] = useState([]);
  const [allPractitioners, setAllPractitioners] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/practitioners", { params: { status: "pending" } }).then((r) => setPractitioners(r.data));
    api.get("/admin/learn", { params: { status: "pending" } }).then((r) => setLearnPending(r.data));
    api.get("/admin/practitioners").then((r) => setAllPractitioners(r.data));
  }, []);

  useEffect(load, [load]);

  const act = async (fn, success) => {
    try { await fn(); toast.success(success); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-12">
      <section>
        <h2 className="font-display text-lg md:text-2xl text-foreground mb-5">Practitioners awaiting review</h2>
        <div className="space-y-3" data-testid="pending-practitioners">
          {practitioners.map((p) => (
            <div key={p.id} className="silk-card rounded-lg p-5" data-testid={`pending-practitioner-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex gap-4">
                  {p.photo && <img src={p.photo} alt={p.name} className="w-14 h-14 rounded-full object-cover" />}
                  <div>
                    <p className="font-display text-xl text-foreground">{p.business_name}</p>
                    <p className="text-sm text-muted-foreground">{p.name} · {p.email}</p>
                    <div className="flex gap-2 mt-2">
                      {(p.category_names?.length ? p.category_names : [p.category_name]).map((n) => <Badge key={n} variant="secondary" className="font-normal">{n}</Badge>)}
                      {p.subcategory_name && <Badge variant="outline" className="font-normal border-primary/40 text-primary">{p.subcategory_name}</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" data-testid={`edit-practitioner-${p.id}`} onClick={() => setEditing(p)}>
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" data-testid={`approve-practitioner-${p.id}`}
                    onClick={() => act(() => api.put(`/admin/practitioners/${p.id}/status`, { status: "approved" }), `${p.business_name} is now live`)}>
                    <Check size={14} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`reject-practitioner-${p.id}`}
                    onClick={() => act(() => api.put(`/admin/practitioners/${p.id}/status`, { status: "rejected" }), "Practitioner rejected")}>
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
              {p.bio && <p className="text-sm text-muted-foreground mt-4 line-clamp-3">{p.bio}</p>}
            </div>
          ))}
          {practitioners.length === 0 && <p className="text-sm text-muted-foreground">Nothing pending — all caught up.</p>}
        </div>
        {editing && <EditPractitionerDialog practitioner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </section>

      <section>
        <h2 className="font-display text-lg md:text-2xl text-foreground mb-5">Learn Library submissions</h2>
        <div className="space-y-3" data-testid="pending-learn">
          {learnPending.map((e) => (
            <div key={e.id} className="silk-card rounded-lg p-5" data-testid={`pending-learn-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display text-xl text-foreground">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.topic} · by {e.author_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`approve-learn-${e.id}`}
                    onClick={() => act(() => api.put(`/admin/learn/${e.id}/status`, { status: "published" }), "Entry published")}>
                    <Check size={14} className="mr-1" /> Publish
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`reject-learn-${e.id}`}
                    onClick={() => act(() => api.put(`/admin/learn/${e.id}/status`, { status: "rejected" }), "Entry rejected")}>
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-3 whitespace-pre-wrap">{e.content}</p>
            </div>
          ))}
          {learnPending.length === 0 && <p className="text-sm text-muted-foreground">No submissions waiting.</p>}
        </div>
      </section>

      <section>
        <h2 className="font-display text-lg md:text-2xl text-foreground mb-5">All practitioners</h2>
        <div className="space-y-2">
          {allPractitioners.map((p) => (
            <div key={p.id} className="silk-card rounded-lg px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{p.business_name} <span className="text-muted-foreground">· {p.name}</span></p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`font-normal text-xs ${p.status === "suspended" ? "text-foreground/80 border-foreground/30" : ""}`}>{p.status}</Badge>
                <span className="text-[11px] text-muted-foreground" data-testid={`listing-until-${p.id}`}>
                  {p.listing_until && new Date(p.listing_until) > new Date()
                    ? `listed until ${new Date(p.listing_until).toLocaleDateString("en-CA")}${p.free_listing_until && new Date(p.free_listing_until) > new Date() ? " · free first year" : ""}`
                    : "no active listing"}
                </span>
                <Button size="sm" variant="ghost" data-testid={`gift-listing-${p.id}`} title="Gift 30 days of free listing"
                  onClick={() => act(() => api.post(`/admin/practitioners/${p.id}/comp-listing`, { days: 30 }), `Gifted 30 days of listing to ${p.business_name}`)}>
                  <Gift size={14} className="mr-1" /> Gift 30d
                </Button>
                {p.status === "approved" && (
                  <Button size="sm" variant="ghost" data-testid={`suspend-practitioner-${p.id}`}
                    onClick={() => act(() => api.put(`/admin/practitioners/${p.id}/status`, { status: "suspended" }), `${p.business_name} is now hidden from the community`)}>
                    <PauseCircle size={14} className="mr-1" /> Suspend
                  </Button>
                )}
                {p.status === "suspended" && (
                  <Button size="sm" variant="outline" data-testid={`reinstate-practitioner-${p.id}`}
                    onClick={() => act(() => api.put(`/admin/practitioners/${p.id}/status`, { status: "approved" }), `${p.business_name} is live again`)}>
                    <PlayCircle size={14} className="mr-1" /> Reinstate
                  </Button>
                )}
                <button title="Toggle featured/verified" data-testid={`feature-toggle-${p.id}`}
                  onClick={() => act(() => api.put(`/admin/practitioners/${p.id}/featured`), "Featured toggled")}
                  className={p.featured ? "text-primary" : "text-muted-foreground hover:text-foreground"}>
                  <Star size={16} fill={p.featured ? "currentColor" : "none"} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
